import CycleMeasurement from '../../enums/cycle-measurement';
import { CycleController } from '../../store/models/controllers';
import { IoTDataResponse } from '../../store/models/cycles';
import getTempByMeasurement from '../../utils/getTempByMeasurement';
import { CycleComponents, NoteSeriesDataType, SourceChartDataType } from '../CycleChartPreview/types';
import { Note } from './types';

export const getNumberNoteSeries = (noteId: string, notes: (Pick<Note, 'id'>)[]) => {
  const index = notes.findIndex((item) => item.id === noteId);
  return index + 1;
};

export const sortNoteSeries = (array: any[]): any[] => {
  return array.sort((a, b) => new Date(a.media.coord[0]).getTime() - new Date(b.media.coord[0]).getTime());
};

export const generateCycleData = (
  notes: Note[],
  iotDataResponse: Record<string, IoTDataResponse[]>,
  measurement: CycleMeasurement,
): CycleComponents => {
  const listNameDevices = Object.keys(iotDataResponse);
  const dimensions = ['time', ...listNameDevices, 'sp'];

  const deviceSeriesData = listNameDevices.map((name) => ({
    name,
    id: name,
  }));

  const iotDataDevices = {};
  const source: SourceChartDataType[] = [];

  const allTimes = new Set<number>();

  const firstChannelKey = Object.keys(iotDataResponse).find((key) => {
    const parts = key.split('#');
    return parts.length > 1 && parts[1].startsWith('C');
  });
  const spByTime: Record<number, number> = {};

  for (const key in iotDataResponse) {
    iotDataDevices[key] = {};

    const deviceData = iotDataDevices[key];
    iotDataResponse[key].forEach((entry) => {
      const unixSeconds = Math.floor(entry.sk / 1000);
      const restoredDate = new Date(unixSeconds * 1000).getTime();
      allTimes.add(restoredDate);

      const deviceTempByTime = getTempByMeasurement(measurement, entry.temp);
      deviceData[restoredDate] = deviceTempByTime;

      if (key === firstChannelKey) {
        spByTime[restoredDate] = getTempByMeasurement(measurement, entry.sp);
      }
    });
  }

  const noteSeriesData: NoteSeriesDataType[] = [];

  for (const key of notes) {
    const { name, coordinate, id, links } = key;
    const unixSeconds = Math.floor(coordinate.x / 1000);
    const restoredDate = new Date(unixSeconds * 1000).getTime();

    const deviceTempByTime = getTempByMeasurement(measurement, coordinate.y);

    iotDataDevices[name] = { [restoredDate]: deviceTempByTime };

    const formattedTime = new Date(restoredDate);

    const linkPoints = (links ?? []).map((link) => {
      const linkUnixSeconds = Math.floor(link.coordinate.x / 1000);
      const linkRestoredDate = new Date(linkUnixSeconds * 1000);
      const linkTemp = getTempByMeasurement(measurement, link.coordinate.y);

      return { id: link.id, coord: [linkRestoredDate, linkTemp] as [Date, number] };
    });

    noteSeriesData.push({
      name,
      id,
      media: { coord: [formattedTime, deviceTempByTime] },
      links: linkPoints,
    });
  }

  const allTimesArray = Array.from(allTimes);
  const sortedTimes = allTimesArray.sort((a, b) => a - b);

  let minTemp: number | null = null;
  let maxTemp: number | null = null;
  
  sortedTimes.forEach((time) => {
    const sourceItem: Record<string, any> = { time };

    if (spByTime[time] !== undefined) {
      sourceItem['sp'] = spByTime[time];
    }
  
    for (const key in iotDataDevices) {
      const entry = iotDataDevices[key]?.[time];
      const temp = entry ?? null;
  
      if (temp !== null) {
        sourceItem[key] = temp;
  
        if (minTemp === null || temp < minTemp) {
          minTemp = temp;
        }
  
        if (maxTemp === null || temp > maxTemp) {
          maxTemp = temp;
        }
      }
    }
  
    source.push(sourceItem as SourceChartDataType);
  });

  return {
    dimensions,
    source,
    deviceSeriesData,
    noteSeriesData: sortNoteSeries(noteSeriesData),
    minTemp,
    maxTemp,
  };
};

export const generateControllersName = (list: CycleController[]): { [key: string]: string } => {
  const controllers = list.map((item) => item.controller);
  const res = controllers.reduce((acc, item) => {
    acc[item.serial_number] = item?.name || item.serial_number;
    return acc;
  }, {});
  return res;
};
